/**
 * SidebarItem.jsx — ELYON-SHELL-FOUNDATION-1
 *
 * Single navigation item in the sidebar.
 * Renders a tooltip in collapsed mode (Radix Tooltip).
 * Badge support for future notification counts.
 * SIDEBAR-IMPORT-GATE: no direct Lucide imports — icon received as prop.
 */
import * as Tooltip from '@radix-ui/react-tooltip';
import { clsx } from 'clsx';
import { NavLink } from 'react-router-dom';

/**
 * @param {{
 *   id: string,
 *   label: string,
 *   icon: import('react').ComponentType,
 *   to: string,
 *   isCollapsed: boolean,
 *   badge?: number,
 * }} props
 */
export function SidebarItem({ id, label, icon: Icon, to, isCollapsed, badge }) {
  const item = (
    <NavLink
      to={to}
      className={({ isActive }) =>
        clsx('elyon-sidebar-item', isActive && 'active')
      }
      data-module-id={id}
      data-testid={`sidebar-item-${id}`}
      style={isCollapsed ? { justifyContent: 'center', padding: 'var(--space-2)' } : undefined}
    >
      <span className="elyon-sidebar-item-icon" aria-hidden="true">
        <Icon size={16} strokeWidth={1.5} />
      </span>

      {!isCollapsed && (
        <span className="elyon-sidebar-item-label">{label}</span>
      )}

      {/* Badge — expanded mode */}
      {!isCollapsed && badge != null && badge > 0 && (
        <span className="elyon-sidebar-badge" aria-label={`${badge} unread`}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}

      {/* Badge dot — collapsed mode */}
      {isCollapsed && badge != null && badge > 0 && (
        <span
          className="elyon-sidebar-badge-dot"
          aria-label={`${badge} unread`}
        />
      )}
    </NavLink>
  );

  // Wrap with tooltip only in collapsed mode
  if (!isCollapsed) return item;

  return (
    <Tooltip.Provider delayDuration={300}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{item}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="right"
            sideOffset={8}
            className="elyon-tooltip-content"
            aria-label={label}
          >
            {label}
            {badge != null && badge > 0 && ` (${badge})`}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
